import {
  CommunityAssessmentData,
  AssessmentSubmissionResponse,
  AssessmentAnalytics,
  AssessmentFormValidationErrors,
} from '@/types/entities/clients/assessment';

class CommunityAssessmentService {
  private baseUrl = '/api/assessment'; // Will be configured when backend is ready

  /**
   * Submit complete community assessment
   */
  async submitAssessment(
    assessmentData: CommunityAssessmentData,
  ): Promise<AssessmentSubmissionResponse> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Validate assessment data
    const validationErrors = this.validateAssessmentData(assessmentData);
    if (Object.keys(validationErrors).length > 0) {
      throw new Error('Assessment validation failed');
    }

    // Generate submission ID
    const submissionId = `ASSESS-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Calculate insights
    const insights = this.generateInsights(assessmentData);

    // Simulate successful submission
    const response: AssessmentSubmissionResponse = {
      success: true,
      submissionId,
      message: `Thank you ${assessmentData.contactName}! Your comprehensive community assessment has been received.`,
      nextSteps: {
        calendarLink: `https://calendly.com/community-app/discovery-${assessmentData.selectedTier}`,
        followUpEmail:
          'Our team will send you a personalized analysis and discovery call agenda within 2 business hours.',
        estimatedResponseTime: '2 business hours',
        personalizedInsights: insights,
      },
    };

    // Track analytics
    this.trackAssessmentSubmission({
      submissionId,
      timestamp: new Date().toISOString(),
      communitySize: assessmentData.totalUnits,
      financialPosition: assessmentData.financialPosition.treasuryAmount,
      primaryChallenges: assessmentData.biggestChallenges,
      selectedTier: assessmentData.selectedTier,
      estimatedValue: insights.potentialROI,
      complexity: insights.implementationComplexity,
    });

    return response;
  }

  /**
   * Save progress (for multi-step form)
   */
  async saveProgress(
    assessmentData: Partial<CommunityAssessmentData>,
    step: number,
  ): Promise<void> {
    const progressKey = `assessment_progress_${Date.now()}`;
    localStorage.setItem(
      progressKey,
      JSON.stringify({
        data: assessmentData,
        step,
        timestamp: new Date().toISOString(),
      }),
    );
  }

  /**
   * Load saved progress
   */
  loadProgress(): { data: Partial<CommunityAssessmentData>; step: number } | null {
    const keys = Object.keys(localStorage).filter((key) => key.startsWith('assessment_progress_'));
    if (keys.length === 0) return null;

    // Get most recent progress
    const latestKey = keys.sort().pop();
    if (!latestKey) return null;

    try {
      const saved = JSON.parse(localStorage.getItem(latestKey) || '{}');
      return { data: saved.data, step: saved.step };
    } catch {
      return null;
    }
  }

  /**
   * Validate assessment data
   */
  private validateAssessmentData(data: CommunityAssessmentData): AssessmentFormValidationErrors {
    const errors: AssessmentFormValidationErrors = {};

    // Basic information validation
    if (!data.communityName?.trim()) {
      errors.communityName = 'Community name is required';
    }
    if (!data.contactName?.trim()) {
      errors.contactName = 'Contact name is required';
    }
    if (!data.contactEmail?.trim()) {
      errors.contactEmail = 'Email address is required';
    }
    if (!data.communityType) {
      errors.communityType = 'Community type is required';
    }
    if (!data.totalUnits) {
      errors.totalUnits = 'Community size is required';
    }

    // Financial validation
    if (!data.financialPosition?.treasuryAmount && data.financialPosition?.treasuryAmount !== 0) {
      errors.treasuryAmount = 'Treasury amount is required for accurate projections';
    }
    if (!data.financialPosition?.monthlyHOAFees) {
      errors.monthlyHOAFees = 'Monthly HOA fees help us understand your financial structure';
    }

    // Stakeholder validation
    if (!data.keyStakeholders?.length) {
      errors.keyStakeholders = 'At least one key stakeholder is required';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (data.contactEmail && !emailRegex.test(data.contactEmail)) {
      errors.contactEmail = 'Please enter a valid email address';
    }

    return errors;
  }

  /**
   * Generate personalized insights based on assessment
   */
  private generateInsights(data: CommunityAssessmentData) {
    const treasuryAmount = data.financialPosition.treasuryAmount || 0;
    const monthlyFees = data.financialPosition.monthlyHOAFees || 0;
    const unitCount = parseInt(data.totalUnits.replace(/\D/g, '')) || 100;

    // Calculate potential ROI based on treasury and fees
    const annualFees = monthlyFees * 12 * unitCount;
    const potentialSavings = annualFees * 0.15; // Conservative 15% savings estimate
    const investmentReturns = treasuryAmount * 0.05; // Conservative 5% investment return
    const potentialROI = potentialSavings + investmentReturns;

    // Determine implementation complexity
    let complexity: 'low' | 'medium' | 'high' = 'medium';
    if (
      data.technologyAssessment?.technologyComfort === 'low' ||
      data.implementationReadiness?.changeManagementNeeds?.length > 3
    ) {
      complexity = 'high';
    } else if (
      data.technologyAssessment?.technologyComfort === 'high' &&
      data.implementationReadiness?.changeManagementNeeds?.length <= 1
    ) {
      complexity = 'low';
    }

    // Recommend features based on challenges and goals
    const recommendedFeatures: string[] = [];

    if (
      data.biggestChallenges?.includes('voting') ||
      data.biggestChallenges?.includes('decision-making')
    ) {
      recommendedFeatures.push('Cryptographic Voting System');
    }

    if (data.financialConcerns?.length > 0 || treasuryAmount > 50000) {
      recommendedFeatures.push('Treasury Management & Investment Tools');
    }

    if (data.communicationIssues?.length > 0) {
      recommendedFeatures.push('Advanced Communication Platform');
    }

    if (data.primaryGoals?.includes('wealth building') || data.investmentInterests?.length > 0) {
      recommendedFeatures.push('Community Investment Opportunities');
    }

    // Estimate timeline based on complexity and tier
    let estimatedTimeline = '4-6 weeks';
    if (complexity === 'high' || data.selectedTier === 'custom') {
      estimatedTimeline = '6-10 weeks';
    } else if (complexity === 'low' && data.selectedTier === 'silver') {
      estimatedTimeline = '3-4 weeks';
    }

    return {
      potentialROI: Math.round(potentialROI),
      recommendedFeatures,
      implementationComplexity: complexity,
      estimatedTimeline,
    };
  }

  /**
   * Track assessment submission analytics
   */
  private trackAssessmentSubmission(analytics: AssessmentAnalytics): void {
    const submissions = JSON.parse(localStorage.getItem('assessment_submissions') || '[]');
    submissions.push(analytics);
    localStorage.setItem('assessment_submissions', JSON.stringify(submissions));

    console.log('Assessment submission tracked:', analytics);
  }

  /**
   * Get assessment by ID
   */
  async getAssessment(submissionId: string): Promise<AssessmentAnalytics | null> {
    const submissions = JSON.parse(localStorage.getItem('assessment_submissions') || '[]');
    return (
      submissions.find((sub: AssessmentAnalytics) => sub.submissionId === submissionId) || null
    );
  }

  /**
   * Get all assessments for admin review
   */
  getAllAssessments(): AssessmentAnalytics[] {
    return JSON.parse(localStorage.getItem('assessment_submissions') || '[]');
  }
}

export const communityAssessmentService = new CommunityAssessmentService();
